import {
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

type IcomCivKind =
  | 'ic7200'
  | 'ic7000'
  | 'ic7100'
  | 'ic746'
  | 'ic7400'
  | 'ic7410'
  | 'ic910'
  | 'ic7300'
  | 'ic7610'
  | 'ic9700'
  | 'ic9700-sat'

type IcomCivSpec = {
  id: string
  model: string
  summary: string
  kind: IcomCivKind
  modelAddress: number
  baudRate: number
  channelCount: number
  nameLength: number
  recordSize: number
  modes: readonly string[]
  banked: boolean
  subCommand: number
  band?: number
}

export type IcomCivProfile = {
  protocol: 'icom-civ'
  id: string
  vendor: 'Icom'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: number
  channelMinimum: 1
  channelMaximum: number
  nameLength: number
  memorySize: number
  kind: IcomCivKind
  modelAddress: number
  recordSize: number
  modes: readonly string[]
  banked: boolean
  subCommand: number
  band?: number
  powerLevels: readonly ['Radio']
}

const SPECS: readonly IcomCivSpec[] = [
  {
    id: 'icom-ic-7200',
    model: 'IC-7200',
    summary: 'Icom IC-7200 HF/6-meter radio using CI-V memory commands.',
    kind: 'ic7200',
    modelAddress: 0x76,
    baudRate: 19200,
    channelCount: 201,
    nameLength: 0,
    recordSize: 12,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'CWR', 'RTTYR'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7000',
    model: 'IC-7000',
    summary: 'Icom IC-7000 HF/VHF/UHF mobile with five memory banks.',
    kind: 'ic7000',
    modelAddress: 0x70,
    baudRate: 19200,
    channelCount: 495,
    nameLength: 9,
    recordSize: 48,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'WFM'],
    banked: true,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7100',
    model: 'IC-7100',
    summary: 'Icom IC-7100 HF/VHF/UHF mobile with five memory banks.',
    kind: 'ic7100',
    modelAddress: 0x88,
    baudRate: 19200,
    channelCount: 495,
    nameLength: 16,
    recordSize: 115,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'WFM', 'CWR', 'RTTYR', 'DV'],
    banked: true,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-746',
    model: 'IC-746',
    summary: 'Icom IC-746 HF/VHF radio with duplicate-tone memory fields.',
    kind: 'ic746',
    modelAddress: 0x56,
    baudRate: 9600,
    channelCount: 99,
    nameLength: 9,
    recordSize: 42,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7400',
    model: 'IC-7400',
    summary: 'Icom IC-7400 HF/VHF/6-meter radio with CI-V memories.',
    kind: 'ic7400',
    modelAddress: 0x66,
    baudRate: 9600,
    channelCount: 99,
    nameLength: 8,
    recordSize: 48,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTYR'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7410',
    model: 'IC-7410',
    summary: 'Icom IC-7410 HF/6-meter radio with data-mode memories.',
    kind: 'ic7410',
    modelAddress: 0x80,
    baudRate: 9600,
    channelCount: 101,
    nameLength: 9,
    recordSize: 43,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTYR', 'Data+LSB', 'Data+USB', 'Data+AM', 'Data+FM'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-910',
    model: 'IC-910',
    summary: 'Icom IC-910 VHF/UHF/23cm radio with banded CI-V memories.',
    kind: 'ic910',
    modelAddress: 0x60,
    baudRate: 19200,
    channelCount: 297,
    nameLength: 0,
    recordSize: 21,
    modes: ['LSB', 'USB', 'CW', 'NCW', 'FM', 'NFM'],
    banked: true,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7300',
    model: 'IC-7300',
    summary: 'Icom IC-7300 HF/6-meter SDR with CI-V memory programming.',
    kind: 'ic7300',
    modelAddress: 0x94,
    baudRate: 115200,
    channelCount: 101,
    nameLength: 10,
    recordSize: 42,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTYR', 'Data+LSB', 'Data+USB', 'Data+AM', 'Data+FM'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-7610',
    model: 'IC-7610',
    summary: 'Icom IC-7610 HF/6-meter SDR with CI-V memory programming.',
    kind: 'ic7610',
    modelAddress: 0x98,
    baudRate: 115200,
    channelCount: 101,
    nameLength: 10,
    recordSize: 28,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTYR', 'Data+LSB', 'Data+USB', 'Data+AM', 'Data+FM'],
    banked: false,
    subCommand: 0x00,
  },
  {
    id: 'icom-ic-9700-2m',
    model: 'IC-9700',
    summary: 'Icom IC-9700 CI-V memories, defaulting to the 2-meter bank.',
    kind: 'ic9700',
    modelAddress: 0xa2,
    baudRate: 19200,
    channelCount: 99,
    nameLength: 16,
    recordSize: 68,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTY-R', 'DV'],
    banked: true,
    subCommand: 0x00,
    band: 1,
  },
  {
    id: 'icom-ic-9700-70cm',
    model: 'IC-9700 (70cm)',
    summary: 'Icom IC-9700 70-centimeter band memory channel set.',
    kind: 'ic9700',
    modelAddress: 0xa2,
    baudRate: 19200,
    channelCount: 99,
    nameLength: 16,
    recordSize: 68,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTY-R', 'DV'],
    banked: true,
    subCommand: 0x00,
    band: 2,
  },
  {
    id: 'icom-ic-9700-23cm',
    model: 'IC-9700 (23cm)',
    summary: 'Icom IC-9700 23-centimeter band memory channel set.',
    kind: 'ic9700',
    modelAddress: 0xa2,
    baudRate: 19200,
    channelCount: 99,
    nameLength: 16,
    recordSize: 68,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTY-R', 'DV', 'DD'],
    banked: true,
    subCommand: 0x00,
    band: 3,
  },
  {
    id: 'icom-ic-9700-satellite',
    model: 'IC-9700 (Satellite)',
    summary: 'Icom IC-9700 satellite memories with independent receive and transmit settings.',
    kind: 'ic9700-sat',
    modelAddress: 0xa2,
    baudRate: 19200,
    channelCount: 99,
    nameLength: 16,
    recordSize: 107,
    modes: ['LSB', 'USB', 'AM', 'CW', 'RTTY', 'FM', 'CWR', 'RTTY-R', 'DV'],
    banked: false,
    subCommand: 0x07,
  },
]

const IMAGE_PREFIX = new TextEncoder().encode('NEWCHIRP-ICOM-CIV\n')
const IMAGE_BYTES_PER_CHANNEL = 420
const CIV_MODES = [
  'LSB',
  'USB',
  'AM',
  'CW',
  'RTTY',
  'FM',
  'WFM',
  'CWR',
  'RTTYR',
  'S-AM',
  'PSK',
  ...Array(13).fill(''),
  'DV',
] as const
const IC9700_MODES = [
  'LSB',
  'USB',
  'AM',
  'CW',
  'RTTY',
  'FM',
  'CWR',
  'RTTY-R',
  ...Array(9).fill(''),
  'DV',
  ...Array(4).fill(''),
  'DD',
] as const

export const ICOM_CIV_PROFILES: readonly IcomCivProfile[] = SPECS.map(
  (spec) => ({
    protocol: 'icom-civ',
    id: spec.id,
    vendor: 'Icom',
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channelCount} memory channels`,
      spec.nameLength
        ? `${spec.nameLength}-character names`
        : 'Frequency-only channels',
    ],
    baudRate: spec.baudRate,
    channelMinimum: 1,
    channelMaximum: spec.channelCount,
    nameLength: spec.nameLength,
    memorySize:
      IMAGE_PREFIX.byteLength +
      spec.channelCount * IMAGE_BYTES_PER_CHANNEL,
    kind: spec.kind,
    modelAddress: spec.modelAddress,
    recordSize: spec.recordSize,
    modes: spec.modes,
    banked: spec.banked,
    subCommand: spec.subCommand,
    band: spec.band,
    powerLevels: ['Radio'],
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'Radio',
    skip: false,
  }
}

function decodeBbcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBbcd(value: number, length: number): Uint8Array {
  const result = new Uint8Array(length)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = length - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function decodeAscii(raw: Uint8Array): string {
  return String.fromCharCode(...raw)
    .replace(/[\u0000\ufffd]/g, '')
    .trim()
}

function encodeAscii(value: string, length: number): Uint8Array {
  const result = new Uint8Array(length).fill(0x20)
  for (let index = 0; index < Math.min(value.length, length); index += 1) {
    result[index] = value.charCodeAt(index) & 0xff
  }
  return result
}

function freqAt(payload: Uint8Array, offset: number): number | null {
  const frequency = decodeLbcd(payload.slice(offset, offset + 5))
  return frequency && frequency >= 1000 ? frequency : null
}

function encodeIcomFrequency(frequency: number): Uint8Array {
  return encodeLbcd(frequency, new Uint8Array(5))
}

function toneChannel(
  channel: WebChannel,
  toneCode: number,
  rtone: number,
  ctone: number,
  dtcs: number,
): WebChannel {
  if (toneCode === 1) {
    return { ...channel, toneMode: 'ctcss', ctcss: rtone, dtcs: null }
  }
  if (toneCode === 2) {
    return { ...channel, toneMode: 'ctcss', ctcss: ctone, dtcs: null }
  }
  if (toneCode === 3) {
    return {
      ...channel,
      toneMode: 'dtcs',
      ctcss: null,
      dtcs,
      dtcsPolarity: 'N',
    }
  }
  return { ...channel, toneMode: 'none', ctcss: null, dtcs: null }
}

function txFor(
  rx: number,
  tx: number | null,
  duplex: number,
  offset: number,
): number {
  if (duplex === 3 && tx) return tx
  if (duplex === 1) return rx - offset
  if (duplex === 2) return rx + offset
  return tx && tx !== rx ? tx : rx
}

function baseChannel(
  number: number,
  payload: Uint8Array,
  rx: number,
  mode: string,
  nameLength: number,
  nameOffset: number,
): WebChannel {
  return {
    number,
    empty: false,
    name: nameLength ? decodeAscii(payload.slice(nameOffset, nameOffset + nameLength)) : '',
    rxFrequency: rx,
    txFrequency: rx,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode,
    power: 'Radio',
    skip: false,
    rawData: btoa(String.fromCharCode(...payload)),
  }
}

function modeName(code: number, profile: IcomCivProfile): string {
  if (profile.kind === 'ic9700' || profile.kind === 'ic9700-sat') {
    return IC9700_MODES[code] || profile.modes[0]
  }
  let value = CIV_MODES[code] || profile.modes[0]
  if (profile.kind === 'ic910') {
    if (value === 'FM' && profile.modes.includes('NFM')) {
      return '(filter2)' // caller resolves after checking filter
    }
  }
  return value
}

function resolveUnifiedMode(
  mode: string,
  filter: number,
): string {
  if (mode === '(filter2)') return filter === 2 ? 'NFM' : 'FM'
  if (filter === 2 && mode === 'CW') return 'NCW'
  return mode
}

function parseIcomPayload(
  profile: IcomCivProfile,
  number: number,
  payload: Uint8Array,
): WebChannel {
  if (!payload.length || payload.at(-1) === 0xff) return emptyChannel(number)
  let channel: WebChannel
  if (profile.kind === 'ic7200') {
    const mode = modeName(payload[8], profile)
    return baseChannel(number, payload, freqAt(payload, 3) ?? 0, mode, 0, 0)
  }
  if (profile.kind === 'ic7000') {
    const rx = freqAt(payload, 4) ?? 0
    const mode = modeName(payload[9], profile)
    const duplexTone = payload[11]
    const duplex = duplexTone >> 4
    const tone = duplexTone & 0x0f
    const rtone = decodeBbcd(payload.slice(12, 15)) / 10
    const ctone = decodeBbcd(payload.slice(15, 18)) / 10
    const dtcs = decodeBbcd(payload.slice(19, 21))
    const tx = freqAt(payload, 21) ?? rx
    const result = baseChannel(number, payload, rx, mode, 9, 38)
    return toneChannel(
      {
        ...result,
        txFrequency: txFor(rx, tx, duplex, 0),
        skip: (payload[3] & 0x0f) !== 1,
      },
      tone,
      rtone,
      ctone,
      dtcs,
    )
  }
  if (profile.kind === 'ic7100') {
    const rx = freqAt(payload, 4) ?? 0
    const mode = modeName(payload[9], profile)
    const duplex = payload[12] >> 4
    const tone = payload[12] & 0x0f
    const rtone = decodeBbcd(payload.slice(14, 17)) / 10
    const ctone = decodeBbcd(payload.slice(17, 20)) / 10
    const offset = decodeBbcd(payload.slice(24, 27)) * 100
    const result = baseChannel(number, payload, rx, mode, 16, 98)
    return toneChannel(
      {
        ...result,
        txFrequency: txFor(rx, null, duplex, offset),
      },
      tone,
      rtone,
      ctone,
      0,
    )
  }
  if (profile.kind === 'ic746') {
    const rx = freqAt(payload, 3) ?? 0
    const mode = modeName(payload[9], profile)
    const duplex = (payload[11] >> 4) & 0x03
    const tone = payload[11] & 0x07
    const rtone = decodeBbcd(payload.slice(13, 15)) / 10
    const ctone = decodeBbcd(payload.slice(16, 18)) / 10
    const result = baseChannel(number, payload, rx, mode, 9, 32)
    return toneChannel(result, tone, rtone, ctone, 0)
  }
  if (profile.kind === 'ic7400') {
    const rx = freqAt(payload, 3) ?? 0
    const mode = modeName(payload[8], profile)
    const duplex = (payload[11] >> 4) & 0x03
    const tone = payload[11] & 0x07
    const rtone = decodeBbcd(payload.slice(12, 15)) / 10
    const ctone = decodeBbcd(payload.slice(15, 18)) / 10
    const dtcs = decodeBbcd(payload.slice(19, 21))
    const tx = freqAt(payload, 21) ?? rx
    const result = baseChannel(number, payload, rx, mode, 8, 39)
    return toneChannel(
      { ...result, txFrequency: txFor(rx, tx, duplex, 0) },
      tone,
      rtone,
      ctone,
      dtcs,
    )
  }
  if (profile.kind === 'ic7410') {
    const rx = freqAt(payload, 3) ?? 0
    const dataMode = (payload[10] & 0x01) !== 0
    let mode = modeName(payload[8], profile)
    if (dataMode && mode === 'LSB') mode = 'Data+LSB'
    if (dataMode && mode === 'USB') mode = 'Data+USB'
    if (dataMode && mode === 'AM') mode = 'Data+AM'
    if (dataMode && mode === 'FM') mode = 'Data+FM'
    const tone = payload[11] & 0x03
    const rtone = decodeBbcd(payload.slice(12, 15)) / 10
    const ctone = decodeBbcd(payload.slice(15, 18)) / 10
    const tx = freqAt(payload, 18) ?? rx
    const result = baseChannel(number, payload, rx, mode, 9, 33)
    return toneChannel(
      { ...result, txFrequency: txFor(rx, tx, tx === rx ? 0 : 3, 0) },
      tone,
      rtone,
      ctone,
      0,
    )
  }
  if (profile.kind === 'ic910') {
    const rx = freqAt(payload, 3) ?? 0
    const mode = resolveUnifiedMode(modeName(payload[8], profile), payload[9])
    const tone = payload[10] >> 4
    const duplex = payload[10] & 0x0f
    const rtone = decodeBbcd(payload.slice(11, 14)) / 10
    const ctone = decodeBbcd(payload.slice(14, 17)) / 10
    const offset = decodeBbcd(payload.slice(17, 20)) * 100
    const result = baseChannel(number, payload, rx, mode, 0, 0)
    return toneChannel(
      { ...result, txFrequency: txFor(rx, null, duplex, offset) },
      tone,
      rtone,
      ctone,
      0,
    )
  }
  if (profile.kind === 'ic7300') {
    const rx = freqAt(payload, 3) ?? 0
    const dataTone = payload[10]
    let mode = modeName(payload[8], profile)
    if ((dataTone >> 4) === 1 && mode === 'LSB') mode = 'Data+LSB'
    if ((dataTone >> 4) === 1 && mode === 'USB') mode = 'Data+USB'
    if ((dataTone >> 4) === 1 && mode === 'AM') mode = 'Data+AM'
    if ((dataTone >> 4) === 1 && mode === 'FM') mode = 'Data+FM'
    const tone = dataTone & 0x0f
    const rtone = decodeBbcd(payload.slice(12, 14)) / 10
    const ctone = decodeBbcd(payload.slice(15, 17)) / 10
    const tx = freqAt(payload, 17)
    const split = (payload[2] >> 4) === 1
    const result = baseChannel(number, payload, rx, mode, 10, 31)
    return toneChannel(
      { ...result, txFrequency: split && tx ? tx : rx },
      tone,
      rtone,
      ctone,
      0,
    )
  }
  if (profile.kind === 'ic7610') {
    const rx = freqAt(payload, 3) ?? 0
    const dataTone = payload[10]
    let mode = modeName(payload[8], profile)
    if ((dataTone >> 4) === 1 && mode === 'LSB') mode = 'Data+LSB'
    if ((dataTone >> 4) === 1 && mode === 'USB') mode = 'Data+USB'
    if ((dataTone >> 4) === 1 && mode === 'AM') mode = 'Data+AM'
    if ((dataTone >> 4) === 1 && mode === 'FM') mode = 'Data+FM'
    const tone = dataTone & 0x0f
    const rtone = decodeBbcd(payload.slice(12, 14)) / 10
    const ctone = decodeBbcd(payload.slice(15, 17)) / 10
    return toneChannel(
      baseChannel(number, payload, rx, mode, 10, 17),
      tone,
      rtone,
      ctone,
      0,
    )
  }
  if (profile.kind === 'ic9700') {
    const rx = freqAt(payload, 4) ?? 0
    const mode = modeName(decodeBbcd(payload.slice(9, 10)), profile)
    const duplex = payload[12] >> 4
    const tone = payload[12] & 0x0f
    const rtone = decodeBbcd(payload.slice(14, 17)) / 10
    const ctone = decodeBbcd(payload.slice(17, 20)) / 10
    const dtcs = decodeBbcd(payload.slice(21, 23))
    const offset = decodeBbcd(payload.slice(24, 27)) * 100
    const result = baseChannel(number, payload, rx, mode, 16, 51)
    return toneChannel(
      { ...result, txFrequency: txFor(rx, null, duplex, offset) },
      tone,
      rtone,
      ctone,
      dtcs,
    )
  }
  const rx = freqAt(payload, 2) ?? 0
  const mode = modeName(decodeBbcd(payload.slice(7, 8)), profile)
  const tone = payload[10] & 0x0f
  const rtone = decodeBbcd(payload.slice(12, 15)) / 10
  const ctone = decodeBbcd(payload.slice(15, 18)) / 10
  const dtcs = decodeBbcd(payload.slice(19, 21))
  const tx = freqAt(payload, 46) ?? rx
  const result = baseChannel(number, payload, rx, mode, 16, 90)
  return toneChannel(
    { ...result, txFrequency: tx },
    tone,
    rtone,
    ctone,
    dtcs,
  )
}

function encodeChannels(
  profile: IcomCivProfile,
  channels: readonly WebChannel[],
): Uint8Array {
  const payload = new TextEncoder().encode(JSON.stringify(channels))
  const image = new Uint8Array(profile.memorySize)
  image.set(IMAGE_PREFIX)
  image.set(payload, IMAGE_PREFIX.byteLength)
  return image
}

function decodeChannels(
  image: Uint8Array,
  profile: IcomCivProfile,
): WebChannel[] {
  const prefix = new TextDecoder().decode(image.slice(0, IMAGE_PREFIX.byteLength))
  if (prefix !== new TextDecoder().decode(IMAGE_PREFIX)) {
    return Array.from({ length: profile.channelMaximum }, (_, index) =>
      emptyChannel(index + 1),
    )
  }
  const payload = new TextDecoder()
    .decode(image.slice(IMAGE_PREFIX.byteLength))
    .replace(/\u0000+$/g, '')
  try {
    const channels = JSON.parse(payload) as WebChannel[]
    const byNumber = new Map(channels.map((channel) => [channel.number, channel]))
    return Array.from(
      { length: profile.channelMaximum },
      (_, index) => byNumber.get(index + 1) ?? emptyChannel(index + 1),
    )
  } catch {
    return Array.from({ length: profile.channelMaximum }, (_, index) =>
      emptyChannel(index + 1),
    )
  }
}

export function parseIcomCivChannels(
  image: Uint8Array,
  profile: IcomCivProfile,
): WebChannel[] {
  return decodeChannels(image, profile)
}

function locationFor(
  profile: IcomCivProfile,
  number: number,
): { bank: number; location: number } {
  if (!profile.banked) return { bank: profile.band ?? 0, location: number }
  if (profile.kind === 'ic9700') {
    return { bank: profile.band ?? 1, location: number }
  }
  return {
    bank: Math.floor((number - 1) / 99) + 1,
    location: ((number - 1) % 99) + 1,
  }
}

function payloadBase(
  profile: IcomCivProfile,
  channel: WebChannel,
): Uint8Array {
  if (channel.rawData) {
    try {
      return Uint8Array.from(atob(channel.rawData), (value) => value.charCodeAt(0))
    } catch {
      // Fall through to a clean record.
    }
  }
  const payload = new Uint8Array(profile.recordSize)
  if (profile.subCommand === 0x07) payload.fill(0x20, 22, 90)
  return payload
}

function setLocation(
  profile: IcomCivProfile,
  payload: Uint8Array,
  number: number,
): void {
  const { bank, location } = locationFor(profile, number)
  const dataOffset = profile.banked ? 1 : 0
  payload.set(encodeBbcd(location, 2), dataOffset)
  if (profile.banked) payload[0] = encodeBbcd(bank, 1)[0]
}

function modeCode(mode: string, profile: IcomCivProfile): number {
  if (profile.kind === 'ic9700' || profile.kind === 'ic9700-sat') {
    const index = IC9700_MODES.indexOf(mode as (typeof IC9700_MODES)[number])
    return Math.max(0, index)
  }
  const baseMode =
    mode === 'NFM'
      ? 'FM'
      : mode === 'NCW'
        ? 'CW'
        : mode.startsWith('Data+')
          ? mode.slice(5)
          : mode
  const index = CIV_MODES.indexOf(baseMode as (typeof CIV_MODES)[number])
  return Math.max(0, index)
}

function toneFields(channel: WebChannel): {
  code: number
  rtone: number
  ctone: number
  dtcs: number
} {
  if (channel.toneMode === 'ctcss') {
    return {
      code: 1,
      rtone: Math.round((channel.ctcss ?? 88.5) * 10),
      ctone: Math.round((channel.ctcss ?? 88.5) * 10),
      dtcs: 0,
    }
  }
  if (channel.toneMode === 'dtcs') {
    return {
      code: 3,
      rtone: 0,
      ctone: 0,
      dtcs: channel.dtcs ?? DTCS_CODES[0],
    }
  }
  return { code: 0, rtone: 0, ctone: 0, dtcs: 0 }
}

function writeTone(
  payload: Uint8Array,
  rtoneOffset: number,
  ctoneOffset: number,
  rtoneLength: number,
  fields: ReturnType<typeof toneFields>,
): void {
  payload.set(encodeBbcd(fields.rtone, rtoneLength), rtoneOffset)
  payload.set(encodeBbcd(fields.ctone, rtoneLength), ctoneOffset)
}

function buildPayload(
  profile: IcomCivProfile,
  channel: WebChannel,
): Uint8Array {
  const payload = payloadBase(profile, channel)
  setLocation(profile, payload, channel.number)
  if (channel.empty) {
    payload.fill(0xff, profile.banked ? 3 : 2)
    return payload
  }
  const rx = channel.rxFrequency ?? 0
  const tx = channel.txFrequency ?? rx
  const offset = Math.abs(tx - rx)
  const duplex = tx > rx ? 2 : tx < rx ? 1 : 0
  const mode = channel.mode
  const tone = toneFields(channel)
  const name = encodeAscii(channel.name, profile.nameLength)
  if (profile.kind === 'ic7200') {
    payload[2] = 0
    payload.set(encodeIcomFrequency(rx), 3)
    payload[8] = modeCode(mode, profile)
    payload[9] = 1
  } else if (profile.kind === 'ic7000') {
    payload[3] = ((tx !== rx ? 1 : 0) << 4) | (channel.skip ? 0 : 1)
    payload.set(encodeIcomFrequency(rx), 4)
    payload[9] = modeCode(mode, profile)
    payload[10] = 1
    payload[11] = ((tx !== rx ? 3 : duplex) << 4) | tone.code
    writeTone(payload, 12, 15, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 19)
    payload.set(encodeIcomFrequency(tx), 21)
    payload[26] = payload[9]
    payload[27] = 1
    payload[28] = ((tx !== rx ? 3 : duplex) << 4) | tone.code
    writeTone(payload, 29, 32, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 36)
    payload.set(name, 38)
  } else if (profile.kind === 'ic7100') {
    payload[3] = 0
    payload.set(encodeIcomFrequency(rx), 4)
    payload[9] = modeCode(mode, profile)
    payload[10] = 1
    payload[12] = (duplex << 4) | tone.code
    writeTone(payload, 14, 17, 3, tone)
    payload.set(encodeBbcd(Math.round(offset / 100), 3), 24)
    payload.set(name, 98)
  } else if (profile.kind === 'ic746') {
    payload[2] = 0
    payload.set(encodeIcomFrequency(rx), 3)
    payload[9] = modeCode(mode, profile)
    payload[11] = (duplex << 4) | tone.code
    writeTone(payload, 13, 16, 2, tone)
    payload.set(name, 32)
  } else if (profile.kind === 'ic7400') {
    payload[2] = 0
    payload.set(encodeIcomFrequency(rx), 3)
    payload[8] = modeCode(mode, profile)
    payload[9] = 1
    payload[11] = (duplex << 4) | tone.code
    writeTone(payload, 12, 15, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 19)
    payload.set(encodeIcomFrequency(tx), 21)
    payload[26] = payload[8]
    payload[27] = 1
    payload[29] = (duplex << 4) | tone.code
    writeTone(payload, 30, 33, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 37)
    payload.set(name, 39)
  } else if (profile.kind === 'ic7410') {
    payload[2] = (tx !== rx ? 1 : 0) << 4
    payload.set(encodeIcomFrequency(rx), 3)
    payload[8] = modeCode(mode.replace(/^Data\+/, ''), profile)
    payload[9] = 1
    payload[10] = mode.startsWith('Data+') ? 1 : 0
    payload[11] = tone.code & 0x03
    writeTone(payload, 12, 15, 3, tone)
    payload.set(encodeIcomFrequency(tx), 18)
    payload[23] = payload[8]
    payload[24] = 1
    payload[25] = payload[10]
    payload[26] = 0
    writeTone(payload, 27, 30, 3, tone)
    payload.set(name, 33)
  } else if (profile.kind === 'ic910') {
    payload[2] = 0
    payload.set(encodeIcomFrequency(rx), 3)
    const [bMode, filter] =
      mode === 'NFM'
        ? [modeCode('FM', profile), 2]
        : mode === 'NCW'
          ? [modeCode('CW', profile), 2]
          : [modeCode(mode, profile), 1]
    payload[8] = bMode
    payload[9] = filter
    payload[10] = (tone.code << 4) | duplex
    writeTone(payload, 11, 14, 3, tone)
    payload.set(encodeBbcd(Math.round(offset / 100), 3), 17)
  } else if (profile.kind === 'ic7300') {
    payload[2] = (tx !== rx ? 1 : 0) << 4
    payload.set(encodeIcomFrequency(rx), 3)
    payload[8] = modeCode(mode.replace(/^Data\+/, ''), profile)
    payload[9] = 1
    payload[10] =
      ((mode.startsWith('Data+') ? 1 : 0) << 4) | (tone.code & 0x0f)
    writeTone(payload, 12, 15, 2, tone)
    payload.set(encodeIcomFrequency(tx), 17)
    payload[22] = payload[8]
    payload[23] = 1
    payload[24] = payload[10]
    writeTone(payload, 26, 29, 2, tone)
    payload.set(name, 31)
  } else if (profile.kind === 'ic7610') {
    payload[2] = 0
    payload.set(encodeIcomFrequency(rx), 3)
    payload[8] = modeCode(mode.replace(/^Data\+/, ''), profile)
    payload[9] = 1
    payload[10] =
      ((mode.startsWith('Data+') ? 1 : 0) << 4) | (tone.code & 0x0f)
    writeTone(payload, 12, 15, 2, tone)
    payload.set(name, 17)
  } else if (profile.kind === 'ic9700') {
    payload[3] = 0
    payload.set(encodeIcomFrequency(rx), 4)
    payload[9] = encodeBbcd(modeCode(mode, profile), 1)[0]
    payload[10] = 1
    payload[11] = 0
    payload[12] = (duplex << 4) | tone.code
    writeTone(payload, 14, 17, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 21)
    payload.set(encodeBbcd(Math.round(offset / 100), 3), 24)
    payload.set(name, 51)
  } else {
    payload.set(encodeIcomFrequency(rx), 2)
    payload[7] = encodeBbcd(modeCode(mode, profile), 1)[0]
    payload[8] = 1
    payload[9] = 0
    payload[10] = (duplex << 4) | tone.code
    writeTone(payload, 12, 15, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 19)
    payload.set(encodeIcomFrequency(tx), 46)
    payload[51] = payload[7]
    payload[52] = 1
    payload[56] = payload[10]
    writeTone(payload, 56, 59, 3, tone)
    payload.set(encodeBbcd(tone.dtcs, 2), 63)
    payload.set(name, 90)
  }
  return payload
}

export function applyIcomCivChannel(
  image: Uint8Array,
  profile: IcomCivProfile,
  channel: WebChannel,
): void {
  const channels = decodeChannels(image, profile)
  channels[channel.number - 1] = channel
  image.set(encodeChannels(profile, channels))
}

function frame(
  profile: IcomCivProfile,
  payload: Uint8Array,
): Uint8Array {
  const result = new Uint8Array(7 + payload.length)
  result.set([
    0xfe,
    0xfe,
    profile.modelAddress,
    0xe0,
    0x1a,
    profile.subCommand,
  ])
  result.set(payload, 6)
  result[result.length - 1] = 0xfd
  return result
}

function modelProbe(profile: IcomCivProfile): Uint8Array {
  return Uint8Array.of(
    0xfe,
    0xfe,
    profile.modelAddress,
    0xe0,
    0x19,
    0x00,
    0xfd,
  )
}

async function readFrame(
  transport: RadioTransport,
  timeoutMs = 1800,
): Promise<Uint8Array> {
  const response = transport.readUntil
    ? await transport.readUntil(0xfd, timeoutMs)
    : await transport.readExact(1, timeoutMs)
  let start = -1
  for (let index = 0; index < response.length - 1; index += 1) {
    if (response[index] === 0xfe && response[index + 1] === 0xfe) {
      start = index
    }
  }
  if (start < 0 || response.length < start + 7) {
    throw new Error('The Icom radio returned an incomplete CI-V frame.')
  }
  return response.slice(start, -1)
}

async function exchange(
  profile: IcomCivProfile,
  transport: RadioTransport,
  payload: Uint8Array,
  echo: boolean,
): Promise<Uint8Array> {
  await transport.write(frame(profile, payload))
  if (echo) await readFrame(transport)
  return readFrame(transport)
}

async function enter(
  profile: IcomCivProfile,
  transport: RadioTransport,
): Promise<boolean> {
  await transport.discardInput()
  const probe = Uint8Array.of(0xfe, 0xfe, 0xe0, 0xe0, 0xfa, 0xfd)
  await transport.write(probe)
  let echo = false
  try {
    const reply = await transport.readExact(probe.length, 250)
    echo = reply.every((value, index) => value === probe[index])
  } catch {
    echo = false
  }
  await transport.write(modelProbe(profile))
  if (echo) await readFrame(transport)
  const identity = await readFrame(transport)
  if (identity.length >= 6 && identity[4] === 0x19) {
    const model = identity[6]
    if (model !== undefined && model !== profile.modelAddress) {
      throw new Error(
        `${profile.model} expects CI-V address 0x${profile.modelAddress.toString(16)}, but the radio reported 0x${model.toString(16)}.`,
      )
    }
  }
  return echo
}

function memoryPayload(
  profile: IcomCivProfile,
  number: number,
): Uint8Array {
  const { bank, location } = locationFor(profile, number)
  if (profile.banked) {
    const payload = new Uint8Array(3)
    payload[0] = encodeBbcd(bank, 1)[0]
    payload.set(encodeBbcd(location, 2), 1)
    return payload
  }
  return encodeBbcd(location, 2)
}

export async function readIcomCivRadioWithTransport(
  profile: IcomCivProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const echo = await enter(profile, transport)
  const channels: WebChannel[] = []
  for (let number = 1; number <= profile.channelMaximum; number += 1) {
    const response = await exchange(
      profile,
      transport,
      memoryPayload(profile, number),
      echo,
    )
    const payload = response.slice(6)
    channels.push(parseIcomPayload(profile, number, payload))
    onProgress?.({
      current: number,
      total: profile.channelMaximum,
      label: `Reading ${profile.model} CI-V memories`,
    })
  }
  return { image: encodeChannels(profile, channels), reportedModel: profile.model }
}

export async function writeIcomCivRadioWithTransport(
  profile: IcomCivProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  const echo = await enter(profile, transport)
  const channels = decodeChannels(image, profile)
  for (let index = 0; index < channels.length; index += 1) {
    const channel = channels[index]
    await exchange(profile, transport, buildPayload(profile, channel), echo)
    onProgress?.({
      current: index + 1,
      total: channels.length,
      label: `Writing ${profile.model} CI-V memories`,
    })
  }
}
